import React from "react";
import type { Transaction } from "../types";
import type { TableCustomization } from "../../../../components/common/tableCustomization";
import { densityPaddingClass, visibleOrderedColumns } from "../../../../components/common/tableCustomization";

interface TransactionsTableProps {
  transactions: Transaction[];
  customization: TableCustomization;
}

const COL_LABELS: Record<string, string> = {
  date: "Date",
  payee: "Payee",
  category: "Category",
  amount: "Amount",
};

const COL_ALIGN: Record<string, "left" | "right"> = {
  date: "left",
  payee: "left",
  category: "left",
  amount: "right",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatAmount(amount: number, direction: "in" | "out", nf: TableCustomization["numberFormat"]): string {
  const symbol = nf.hideCurrencySymbol ? "" : "$";
  const abs = Math.abs(amount);
  const str = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (direction === "out") {
    return nf.negativeStyle === "parentheses" ? `(${symbol}${str})` : `-${symbol}${str}`;
  }
  return `+${symbol}${str}`;
}

/**
 * Desktop: full table with customizable columns.
 * Mobile: card list (payee + amount on top row, date + category below).
 */
export function TransactionsTable({ transactions, customization }: TransactionsTableProps) {
  if (transactions.length === 0) return null;

  const visibleCols = visibleOrderedColumns(customization);
  const padY = densityPaddingClass(customization.density);
  const nf = customization.numberFormat;

  const renderCell = (col: string, t: Transaction): React.ReactNode => {
    switch (col) {
      case "date":
        return <span className="text-ink3 tabular-nums whitespace-nowrap">{formatDate(t.date)}</span>;
      case "payee":
        return <span className="text-ink2">{t.cleanDescription || t.rawDescription}</span>;
      case "category":
        return (
          <span className={t.status === "review" ? "text-amber font-semibold" : "text-ink2"}>
            {t.status === "review" ? "Needs review" : t.category || ""}
          </span>
        );
      case "amount":
        return (
          <span className={`text-right font-semibold tabular-nums ${t.direction === "in" ? "text-teal" : "text-ink"}`}>
            {formatAmount(t.amount, t.direction, nf)}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Desktop table */}
      <table className="hidden md:table w-full border-collapse text-[12.5px]">
        <thead>
          <tr>
            {visibleCols.map((v) => {
              const col = v.key;
              const align = COL_ALIGN[col] ?? "left";
              return (
                <th
                  key={col}
                  className={`${align === "right" ? "text-right" : "text-left"} text-[10.5px] font-bold uppercase tracking-wider text-ink3 p-2.5 border-b border-line`}
                >
                  {COL_LABELS[col]}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {transactions.map((t) => (
            <tr key={t.id} className="hover:bg-surface-paper cursor-pointer">
              {visibleCols.map((v) => {
                const col = v.key;
                const align = COL_ALIGN[col] ?? "left";
                return (
                  <td key={col} className={`${padY} px-2.5 border-b border-lineSoft ${align === "right" ? "text-right" : ""}`}>
                    {renderCell(col, t)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile card list */}
      <div className="md:hidden flex flex-col">
        {transactions.map((t) => (
          <div key={t.id} className="py-3 border-b border-lineSoft cursor-pointer">
            <div className="flex justify-between items-baseline gap-2 mb-0.5">
              <span className="text-[13px] font-semibold text-ink">{t.cleanDescription || t.rawDescription}</span>
              <span className={`text-[13.5px] font-bold tabular-nums ${t.direction === "in" ? "text-teal" : "text-ink"}`}>
                {formatAmount(t.amount, t.direction, nf)}
              </span>
            </div>
            <div className="text-[11px] text-ink3">
              {formatDate(t.date)} · {t.status === "review" ? <span className="text-amber font-semibold">Needs review</span> : (t.category || "Uncategorized")}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}